/**
 * board-layout — the trial board's list ↔ Kanban layout choice, persisted per
 * browser session.
 *
 * The choice is a view preference, not application state, so it lives in
 * `sessionStorage` (cleared when the tab closes) rather than the URL or the
 * polled snapshots: it survives a nav between views and a soft reload within the
 * session without leaking into shareable deep links. Every access is guarded so
 * the SPA degrades to the default `list` layout in a context without
 * `sessionStorage` (private-mode quota, SSR, tests) instead of throwing.
 */

/** The two board layouts the trial-fidelity view can render in. */
export type BoardLayout = 'list' | 'kanban'

const STORAGE_KEY = 'evaluator.board.layout'

/** Read the persisted layout, defaulting to `list` when unset or unavailable. */
export function readLayout(): BoardLayout {
  try {
    return globalThis.sessionStorage?.getItem(STORAGE_KEY) === 'kanban' ? 'kanban' : 'list'
  } catch {
    return 'list'
  }
}

/** Persist the chosen layout for the rest of the session; a no-op if storage is unavailable. */
export function writeLayout(layout: BoardLayout): void {
  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, layout)
  } catch {
    // sessionStorage can throw (disabled / over quota) — the layout simply
    // won't persist; the in-memory state still drives this session's render.
  }
}
